import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
  AppBar,
  Button,
  Dialog,
  IconButton,
  Typography,
  Toolbar,
  DialogContent,
  TextField,
  DialogActions,
  Box,
  Checkbox,
  FormGroup,
  FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useUnit } from 'effector-react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
   $accountListFiltered,
  $openModal, accountGetListFX,
  createBoardForSelWSFx,
  setOpenModal,
} from '../../model/board';
import { $selectWorkspaceData } from '../../model/workspace';
import { Account, PCreate } from '../../../apiRPC/board';
import { Members } from '../../pages/desk/modals/TaskOverview/Members';
import { MenuMembers } from '../../pages/desk/Card/Members/MenuMembers';




export const CreateBoard: FC = () => {
  const accounts = useUnit($accountListFiltered);

  const formik = useFormik<
    Omit<PCreate, 'workspace' | 'allowedAccounts'> & {
      allowedAccounts?: Account[];
    }
  >({
    initialValues: {
      title: '',
      hasRestrictedAccess: false,
      allowedAccounts: undefined,
      cardsHaveCyrillicTitles: true,
    },
    validationSchema: Yup.object({
      title: Yup.string()
        .required('Обязательное поле')
        .trim()
        .min(3, 'Минимум 3 знака'),
      hasRestrictedAccess: Yup.boolean(),
      cardsHaveCyrillicTitles: Yup.boolean(),
      allowedAccounts: Yup.array()
        .of(Yup.object().shape({
          uuid: Yup.string().required(),
          email: Yup.string().required(),
        }))
        .when('hasRestrictedAccess', {
          is: true,
          then: (schema) => schema.required('Должен быть выбран хотя бы один аккаунт').min(1, 'Должен быть выбран хотя бы один аккаунт'),
          otherwise: (schema) => schema,
        }),
    }),
    validateOnBlur: false,
    validateOnChange: true,
    validateOnMount: false,
    onSubmit: (values, f) => {
      setOpenModal(false);
      f.resetForm();

      const data: Omit<PCreate, 'workspace'> = {
        title: values.title,
        hasRestrictedAccess: values.hasRestrictedAccess,
        allowedAccounts: values.hasRestrictedAccess
          ? values.allowedAccounts?.map((item) => item.uuid)
          : undefined,
        cardsHaveCyrillicTitles: values.cardsHaveCyrillicTitles,
      };
      createBoardForSelWSFx(data);
    },
  });

  useEffect(() => {
    accountGetListFX({
      filter: {
        status: 'ACTIVE',
      },
    })
  }, []);

  const onClose = useCallback(() => {
    setOpenModal(false);
    formik.resetForm();
  }, []);
  const open = useUnit($openModal);
  const selectWorkspaceData = useUnit($selectWorkspaceData);


  const btnRef = React.useRef<HTMLButtonElement>(null);
  const copyBtnRef = React.useRef<HTMLElement|null>(null);

  const [openMenuMembers, setOpenMenuMembers] = useState(false);


  const onOpenMenuMembers =()=> {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'fixed';
      tempDiv.style.top = `0px`;
      tempDiv.style.left = `${rect.left}px`;
      tempDiv.style.width = `${rect.width}px`;
      tempDiv.style.height = `${rect.height}px`;
      document.body.appendChild(tempDiv);
      copyBtnRef.current = tempDiv;
    }
    setOpenMenuMembers(true);
  }
  const onCloseMenuMembers = () => {
    setOpenMenuMembers(false);
    if (copyBtnRef.current) {
      document.body.removeChild(copyBtnRef.current);
      copyBtnRef.current = null;
    }
  };

  const onClickMember = (member: Account, isChecked: boolean) => {

    if (isChecked) {
      formik.setFieldValue('allowedAccounts', [...(formik.values.allowedAccounts??[]).filter(item=>item.uuid!==member.uuid)],true)
      return;
    }
    formik.setFieldValue('allowedAccounts', [...(formik.values.allowedAccounts??[]),member],true)
  };



  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar disableGutters>
          <Typography
            sx={{
              ml: 2,
              flex: 1,
              wordWrap: 'break-word',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
            variant="h6"
            component="div"
          >
            Новая доска в рабочем пространстве {selectWorkspaceData?.title}
          </Typography>
          <IconButton
            sx={{ alignSelf: 'center', marginLeft: 'auto' }}
            edge="start"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <DialogContent>
        <Box component="form" onSubmit={formik.handleSubmit} noValidate mt={1}>
          <TextField
            autoFocus
            required
            margin="dense"
            id="title"
            label="Название"
            type="text"
            fullWidth
            variant="standard"
            onChange={formik.handleChange}
            value={formik.values.title}
            onBlur={formik.handleBlur}
            error={Boolean(formik.errors.title)}
            helperText={formik.errors.title || ' '}
            inputProps={{ maxLength: 255 }}
          />
          <FormGroup>
            <FormControlLabel
              control={<Checkbox
                checked={formik.values.hasRestrictedAccess}
                onChange={formik.handleChange}
                name="hasRestrictedAccess"
              />}
              label="Сделать доску приватной"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formik.values.cardsHaveCyrillicTitles}
                  onChange={formik.handleChange}
                  name="cardsHaveCyrillicTitles"
                />
              }
              label="Разрешить кириллицу в заголовках карточек"
            />
            {formik.values.hasRestrictedAccess &&  <Members title="Доступно для:" members={formik.values.allowedAccounts??[]} onAdd={onOpenMenuMembers} btnRef={btnRef}/>}

            <Typography variant="body2" color="red" fontWeight={400}>
              {formik.errors.allowedAccounts}
            </Typography>
            <MenuMembers
              openMenu={openMenuMembers}
              anchorEl={copyBtnRef?.current}
              handleClose={onCloseMenuMembers}
              accounts={accounts??[]}
              listChecked={formik.values.allowedAccounts?.map(item=>item.uuid) || []}
              onClickMember={onClickMember}
              headerTitle="Доступно для"
              titleList=""
              onClearAll={
                () => {
                  formik.setFieldValue('allowedAccounts', [], true);
                }
              }
              onSelectAll={
                () => {
                  formik.setFieldValue('allowedAccounts', accounts?.map(({uuid,email})=>({uuid,email})), true);
                }
              }
            />
          </FormGroup>
        </Box>

      </DialogContent>
      <DialogActions>
        <Button onClick={formik.submitForm} disabled={Boolean(Object.values(formik.errors).length)}>Создать</Button>
      </DialogActions>
    </Dialog>
  );
};
